import React from 'react'
import { AvatarWithoutDot } from '@components/Avatar/Avatar'
import { ReviewStars } from '@components/ReviewStars/ReviewStars'
import { userIdFromAddress } from '@towns-protocol/sdk'
import { bin_fromHexString } from '@towns-protocol/utils'
import { Box, Stack, Text } from '@ui'
import { useReviews } from 'hooks/useReviews'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { shortAddress } from 'ui/utils/utils'
import { Address, useGetRootKeyFromLinkedWallet, useUserLookup } from 'use-towns-client'
import { formatRelativeTime } from 'utils/formatDates'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

export interface ReviewItemProps {
    review: ReturnType<typeof useReviews>['reviews'][number]
    showActions?: boolean
    renderActions?: (reviewId: string) => React.ReactNode
    clickableUser?: boolean
    handleOpenProfile?: () => void
}

const AuthorDisplay = ({
    address,
    clickableUser,
    handleOpenProfile,
}: {
    address: string
    clickableUser?: boolean
    handleOpenProfile?: () => void
}) => {
    const { data: rootKeyAddress } = useGetRootKeyFromLinkedWallet({ walletAddress: address })
    const user = useUserLookup(rootKeyAddress ?? '')
    const name = !user ? shortAddress(address) : getPrettyDisplayName(user)

    return (
        <Box
            tooltip={address}
            cursor={clickableUser ? 'pointer' : 'default'}
            onClick={handleOpenProfile}
        >
            <Text strong>{name}</Text>
        </Box>
    )
}

export const ReviewItem: React.FC<ReviewItemProps> = ({
    review,
    showActions = false,
    renderActions,
    clickableUser = false,
}) => {
    const { id, author, rating, text, createdAt } = review
    const { openPanel } = usePanelActions()
    const { data: rootKeyAddress } = useGetRootKeyFromLinkedWallet({
        walletAddress: author as Address,
    })
    const userId = rootKeyAddress ? userIdFromAddress(bin_fromHexString(rootKeyAddress)) : undefined

    const handleOpenProfile = () => {
        if (clickableUser && userId) {
            openPanel('profile', { profileId: userId })
        }
    }

    return (
        <Stack position="relative">
            <Stack horizontal gap="sm" alignItems="start">
                <Box cursor={clickableUser ? 'pointer' : 'default'} onClick={handleOpenProfile}>
                    <AvatarWithoutDot userId={rootKeyAddress} size="avatar_md" />
                </Box>
                <Stack grow gap="sm">
                    <ReviewStars rating={rating} size={16} />
                    <Text>{text}</Text>
                    <Stack horizontal gap="sm" alignItems="center">
                        <AuthorDisplay
                            address={author}
                            clickableUser={clickableUser}
                            handleOpenProfile={handleOpenProfile}
                        />
                        {createdAt > 0 && (
                            <Text color="gray2">{formatRelativeTime(createdAt, true)}</Text>
                        )}
                    </Stack>
                </Stack>
            </Stack>

            {showActions && renderActions && (
                <Box
                    pointerEvents="auto"
                    position="absolute"
                    right="xs"
                    top="-x4"
                    zIndex="tooltips"
                >
                    {renderActions(id)}
                </Box>
            )}
        </Stack>
    )
}
